import { getMicrosoftCredentials } from '@/cloudFunctions/microsoft';
import { useFirm } from '@/database/hooks/hooks';
import { useEffect, useState } from 'react';
import ObjectFieldsSection from './ObjectFieldsSection';
import { getGoogleCredentials } from '@/cloudFunctions/googleWorkspace';

const EmailProviderSection = ({ opened }: { opened: boolean }) => {
  const { firm } = useFirm();

  if (firm?.emailProvider === 'microsoft') {
    return <MicrosoftTokenSection opened={opened} />;
  }

  if (firm?.emailProvider === 'google') {
    return <GoogleTokenSection opened={opened} />;
  }

  return null;
};

export default EmailProviderSection;

interface MicrosoftTokenInfo {
  username: string;
  tenantId: string;
  expiresOn: number;
  accessToken: string;
}

const MicrosoftTokenSection = ({ opened }: { opened: boolean }) => {
  const { firm } = useFirm();
  const [tokenInfo, setTokenInfo] = useState<MicrosoftTokenInfo>();
  const [tokenError, setTokenError] = useState<string>();

  useEffect(() => {
    if (opened && firm?.emailProvider === 'microsoft') {
      getMicrosoftCredentials()
        .then((result) => {
          if (result.status === 'success') {
            setTokenInfo({
              username: result.payload.credentials.account.username,
              tenantId: result.payload.credentials.account.tenantId,
              expiresOn: result.payload.credentials.expiresOn,
              accessToken: result.payload.credentials.accessToken,
            });
            setTokenError(undefined);
          } else {
            setTokenInfo(undefined);
            setTokenError(
              result.status === 'error' ? result.error : 'No credentials',
            );
          }
        })
        .catch((err) => {
          setTokenError(err?.message || 'Failed to load Microsoft token');
        });
    } else {
      setTokenInfo(undefined);
      setTokenError(undefined);
    }
  }, [opened, firm?.emailProvider]);

  return (
    <ObjectFieldsSection
      document={tokenError ? { error: tokenError } : tokenInfo}
      title='Microsoft Token'
    />
  );
};

interface GoogleTokenInfo {
  accessToken: string;
  expiryDate?: number;
  tokenType?: string;
  scope?: string;
}
const GoogleTokenSection = ({ opened }: { opened: boolean }) => {
  const { firm } = useFirm();
  const [tokenInfo, setTokenInfo] = useState<GoogleTokenInfo>();
  const [tokenError, setTokenError] = useState<string>();

  useEffect(() => {
    if (opened && firm?.emailProvider === 'google') {
      getGoogleCredentials()
        .then((result) => {
          if (result.status === 'success') {
            setTokenInfo({
              accessToken: result.payload.credentials.accessToken,
              expiryDate: result.payload.credentials.expiryDate,
              tokenType: result.payload.credentials.tokenType,
              scope: result.payload.credentials.scope,
            });
            setTokenError(undefined);
          } else {
            setTokenInfo(undefined);
            setTokenError(
              result.status === 'error' ? result.error : 'No credentials',
            );
          }
        })
        .catch((err) => {
          setTokenError(err?.message || 'Failed to load Google token');
        });
    } else {
      setTokenInfo(undefined);
      setTokenError(undefined);
    }
  }, [opened, firm?.emailProvider]);

  return (
    <ObjectFieldsSection
      document={tokenError ? { error: tokenError } : tokenInfo}
      title='Google Token'
    />
  );
};
